import { message, Form, Input, Switch } from 'antd';
import Card, { CardEdit } from 'components/Card/Card';
import { useEditCard } from 'components/Card/useEditCard';
import TabularList from 'components/TabularList/TabularList';
import { useAddressFieldsData } from 'components/AddressFields/AddressFieldsFlat';
import PhoneNumber from 'components/PhoneNumber';
import FormItem from 'components/FormItem';
import Message from 'components/ui/Message';
import { useEffect } from 'react';
import { providerService } from 'services';
import { getFormattedPhoneNumber, getPhoneEXTValidator, getPhoneNumberValidator } from 'utils';
import FormattedAddress from 'components/FormattedAddress/FormattedAddress';
import classNames from 'classnames';

export function CareLocationAddressAndPhone({ application, careFacility, setCareFacility, isEditable = false }) {
  const [form] = Form.useForm();

  const { isEditing, editProps } = useEditCard({
    id: 'care-location-address-phone',
    onEditStart: () =>
      form.setFieldsValue({
        locationAddressSameAsOrg: careFacility?.locationAddressSameAsOrg,
        address: careFacility?.address,
        facilityPhone: careFacility?.facilityPhone,
        facilityPhoneExt: careFacility?.facilityPhoneExt,
      }),
    onSave: async () => {
      await form.validateFields();
      const values = form.getFieldsValue();
      const payload = {
        locationAddressSameAsOrg: values.locationAddressSameAsOrg,
        address: values.address,
        facilityPhone: values.facilityPhone,
        facilityPhoneExt: values.facilityPhoneExt,
      };
      await providerService.updateCenter(payload, careFacility?.id);
      const updatedCareFacility = await providerService.getCenterById(Number(careFacility?.id));
      setCareFacility(updatedCareFacility);
      form.resetFields();
      message.success('Care location address and phone updated');
    },
    onDiscard: () => form.resetFields(),
  });

  const locationAddressSameAsOrg = Form.useWatch('locationAddressSameAsOrg', form);

  // When "Same as organization" is toggled on, copy organization's address and phone
  useEffect(() => {
    if (isEditing && locationAddressSameAsOrg && careFacility?.organization?.address) {
      const orgAddress = careFacility.organization.address;
      const orgPhone = careFacility.organization.businessPhone;
      const orgPhoneExt = careFacility.organization.businessPhoneExt;

      form.setFieldsValue({
        address: {
          street: orgAddress.street,
          street2: orgAddress.street2,
          city: orgAddress.city,
          state: orgAddress.state,
          postalCode: orgAddress.postalCode,
        },
        facilityPhone: orgPhone,
        facilityPhoneExt: orgPhoneExt,
      });
    }
  }, [isEditing, locationAddressSameAsOrg, careFacility, form]);

  const addressFieldsData = useAddressFieldsData({
    form,
    addressKey: 'address',
    isEditing,
    addressValue: careFacility?.address,
    disabled: locationAddressSameAsOrg || editProps.loading,
  });

  const showAddressSummary = !isEditing && careFacility?.locationAddressSameAsOrg;

  return (
    <Card
      className="mt-8"
      title="Care location address and phone"
      stickyHeader
      extra={isEditable ? <CardEdit {...editProps} /> : null}
      noBodyPadding
    >
      <Form form={form} disabled={editProps.loading}>
        <TabularList
          indented
          data={[
            ...(showAddressSummary
              ? []
              : [
                  {
                    title: 'Same as organization',
                    value: isEditing ? (
                      <Form.Item name="locationAddressSameAsOrg" valuePropName="checked" className="mb-0">
                        <Switch />
                      </Form.Item>
                    ) : careFacility?.locationAddressSameAsOrg ? (
                      'Yes'
                    ) : (
                      'No'
                    ),
                  },
                ]),
            ...(showAddressSummary || (isEditing && locationAddressSameAsOrg) ? [] : addressFieldsData),
            ...(showAddressSummary || (isEditing && locationAddressSameAsOrg)
              ? []
              : [
                  {
                    title: 'Phone',
                    titleRequired: true,
                    value: isEditing ? (
                      <FormItem
                        name="facilityPhone"
                        rules={[
                          { required: true, message: 'Facility Phone Number is required.' },
                          getPhoneNumberValidator('Invalid Phone Number'),
                        ]}
                        className="mb-0"
                      >
                        <PhoneNumber
                          aria-label="facility phone number"
                          disabled={locationAddressSameAsOrg}
                          data-testid="facilityPhoneNumber"
                        />
                      </FormItem>
                    ) : (
                      getFormattedPhoneNumber(careFacility?.facilityPhone)
                    ),
                  },
                  {
                    title: 'Extension',
                    value: isEditing ? (
                      <FormItem
                        name="facilityPhoneExt"
                        rules={[getPhoneEXTValidator('Invalid Extension.')]}
                        className="mb-0"
                      >
                        <Input aria-label="facility phone number extension" data-testid="facilityPhoneNumberExt" />
                      </FormItem>
                    ) : careFacility?.facilityPhoneExt ? (
                      careFacility?.facilityPhoneExt
                    ) : (
                      <span className="italic text-gray-400">Not provided</span>
                    ),
                  },
                ]),
          ]}
        />

        {(showAddressSummary || (isEditing && locationAddressSameAsOrg)) && (
          <Message
            title="Using organization's address and phone"
            className={classNames('mx-6 mb-6', isEditing ? '' : 'mt-6')}
          >
            <div className="space-y-2">
              <FormattedAddress address={careFacility?.organization?.address} />
              <div>
                {getFormattedPhoneNumber(careFacility?.organization?.businessPhone)}
                {careFacility?.organization?.businessPhoneExt &&
                  ` - Ext. ${careFacility?.organization?.businessPhoneExt}`}
              </div>
            </div>
          </Message>
        )}
      </Form>
    </Card>
  );
}
